import { LinkExternal02 } from '@kula-ai/icons/outline/LinkExternal02';
import { jobStatusConfig } from '@/constants/job-constants';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';
import { atsJobsApiActions } from '@/store/atsJobs';
import { Box } from '@chakra-ui/react';
import * as Yup from 'yup';
import { FormBuilder } from '@/components/formBuilder';
import { KText } from '@/ui-library/components/text';
import { SelectOptionWithAvatar } from '@/components/formBuilder/SelectComponents/SelectOptionWithAvatar';
import {
  SelectParentChildRootOption,
  DepartmentMultiValue,
} from '@/components/formBuilder/SelectComponents';
import { jobsEmploymentsOptions } from '@/constants/job-constants';

type FetchJobsWithFilterParams = {
  currentView?: any;
  filters?: any;
  sort?: any;
  page?: any;
  searchTerm?: any;
  dispatch?: any;
  type?: string;
  isBackground?: boolean;
};

export const fetchJobsWithFilter = ({
  currentView,
  filters,
  sort,
  page,
  searchTerm,
  dispatch,
  type,
  isBackground = false,
}: FetchJobsWithFilterParams = {}) => {
  const {
    office_ids = [],
    department_ids = [],
    job_statuses = [],
    user_ids = [],
    eeoc_data_collection = [],
    beConfig = {},
  } = filters || {};

  let updatedJobStatus = [...job_statuses];

  if (type === 'jobStage') {
    updatedJobStatus = [{ id: 'published' }, { id: 'scheduled' }];
  }

  // Build filter_by from old format (fallback for backward compatibility)
  const oldFormatFilters = {
    office_ids: office_ids.map((i) => i.id),
    department_ids: department_ids.map((i) => i.id),
    job_statuses: updatedJobStatus?.map((i) => i.id),
    user_ids: user_ids.map((i) => i.id),
    eeoc_data_collection: eeoc_data_collection?.[0]?.id,
  };

  // beConfig preserves condition objects (e.g. { is: [392] }, { is_not: [804] })
  // and is spread directly into filter_by
  const filter_by = {
    ...oldFormatFilters,
    ...beConfig,
  };

  // Clean up empty/undefined values
  Object.keys(filter_by).forEach((key) => {
    const value = filter_by[key];
    if (
      value === undefined ||
      value === null ||
      (Array.isArray(value) && value.length === 0)
    ) {
      delete filter_by[key];
    }
  });

  const params = {
    view: currentView,
    sort_by: {
      [`${sort?.element}`]: sort?.order,
    },
    filter_by,
    q: searchTerm,
    limit: page?.limit,
    offset: page?.offset,
  };
  dispatch(
    atsJobsApiActions('getAllJobs', {
      requestConfig: { params },
      isBackground,
    })
  );
};

const archiveFormFieldConfig = [
  {
    name: 'archiveInput',
    type: 'input',
    isRequired: true,
    placeholder: 'Type "ARCHIVE"',
    errorMessage: 'Confirmation message is required',
    validation: Yup.string()
      .oneOf(['ARCHIVE'], `Confirmation message doesn't match`)
      .required('Confirmation message is required'),
  },
];

const INACTIVE_JOB_STATES = ['archived', 'closed'];
const ARCHIVABLE_JOB_STATES = ['draft', 'closed'];

export const jobsTableRowConfig = (
  history,
  jobDetails,
  toast,
  dispatch,
  type = 'table',
  formRef,
  refetchJobs = () => {},
  isCreateAllowed = false,
  _feJobListingChanges
) => {
  // The card is built per job; the table is built once for the grid and gets
  // its job per row at call time.
  const resolveJob = (row) => (type === 'card' ? jobDetails : row) || {};
  const isActive = (row) =>
    !INACTIVE_JOB_STATES.includes(resolveJob(row)?.state);

  const config = [
    {
      id: 'edit',
      label: 'Edit job',
      enabled: (row) => Boolean(resolveJob(row)?.can_edit),
      isConditional: true,
      checkCondition: isActive,
      onClick: (onClose, currentClickedRow) => {
        const job = resolveJob(currentClickedRow);
        const tab = job?.state === 'draft' ? 'jobInformation' : 'settings';
        history.push(`/jobs/${job?.id}?tab=${tab}`);
        onClose();
      },
    },
    {
      id: 'preview',
      label: 'Preview job',
      icon: LinkExternal02,
      iconAlignRight: true,
      enabled: (row) => {
        const job = resolveJob(row);
        return !(job?.job_post_kind === 'internal' || job?.state === 'draft');
      },
      isConditional: true,
      checkCondition: isActive,
      onClick: async (onClose, currentClickedRow) => {
        try {
          const jobId = resolveJob(currentClickedRow)?.id;
          const apiConfig = {
            processResponse: (res) => res,
            endPoint: `ats_jobs/${jobId}/ats_job_post/preview`,
          };
          const response = await fetchSelectOptions(apiConfig);
          const { errors } = response || {};
          const { preview_url } = response?.data || {};
          if (preview_url) {
            window.open(preview_url, '_blank');
            onClose();
          }
          if (errors?.length > 0) {
            toast({
              title: `Please save draft to get preview`,
              description: '',
              status: 'error',
            });
          }
        } finally {
          // setPreviewLoading(false);
        }
      },
    },
    {
      id: 'clone',
      label: 'Clone job',
      enabled: isCreateAllowed,
      modalConfig: {
        title: 'Clone job',
        // description: 'Please confirm to clone this job',
        successButtonText: 'Clone',
        cancelButtonText: 'Cancel',
        isActionLoading: false,
        handleOnClose: (onClose) => {
          onClose();
        },
        isModalActionLoading: false,
        isButtonDisabled: false,
        onModalSubmit: async (currentClickedRow, onClose, loadingAction) => {
          const jobId = resolveJob(currentClickedRow)?.id;

          loadingAction(true);
          try {
            const response = await dispatch(
              atsJobsApiActions('cloneJob', {
                pathParams: { jobId: jobId },
                isApi: true,
              })
            );
            if (response?.id) {
              toast({ title: 'Job cloned successfully' });
              onClose();
              history.push(`/jobs/${response?.id}?isClonedJob=true`);
            } else {
              toast({
                title: 'Error in cloning job',
                status: 'error',
              });
            }
          } finally {
            loadingAction(false);
          }
        },
        RenderContent: ({ selected }) => {
          const jobName = resolveJob(selected)?.name;
          return (
            <KText fontSize="sm" color="secondary-700">
              Cloning this job
              <KText as="span" size="sm-semi-bold" color="secondary-700">
                {' '}
                {jobName}{' '}
              </KText>
              will create a duplicate without any candidates
            </KText>
          );
        },
      },
    },
    {
      id: 'archive',
      label: 'Archive job',
      enabled: (row) => Boolean(resolveJob(row)?.can_edit),
      isConditional: true,
      checkCondition: (row) =>
        ARCHIVABLE_JOB_STATES.includes(resolveJob(row)?.state),
      modalConfig: {
        title: 'Are you sure?',
        description: `Archiving a job is permanent and cannot be undone`,
        successButtonText: 'Archive',
        cancelButtonText: 'Cancel',
        isActionLoading: false,
        handleOnClose: (onClose) => {
          onClose();
        },
        isModalActionLoading: false,
        isButtonDisabled: false,
        onModalSubmit: (selected, onClose, loadingAction) => {
          if (formRef?.current) {
            const currentValues = formRef.current.values;
            const newValues = {
              ...currentValues,
              selected,
              onClose,
              loadingAction,
            };
            formRef?.current?.setValues(newValues);
            formRef?.current?.submitForm();
          }
        },
        RenderContent: () => (
          <Box>
            <FormBuilder
              config={archiveFormFieldConfig}
              onSubmit={async (values) => {
                const {
                  selected = {},
                  onClose = () => {},
                  loadingAction = () => {},
                } = values;
                loadingAction(true);
                try {
                  const response = await dispatch(
                    atsJobsApiActions('archiveJob', {
                      pathParams: { jobId: resolveJob(selected)?.id },
                      isApi: true,
                    })
                  );
                  if (!response?.errors?.length) {
                    toast({ title: 'Job archived successfully' });
                    onClose();
                    refetchJobs();
                  } else {
                    toast({
                      title: 'Error in archiving a job',
                      status: 'error',
                    });
                  }
                } catch (error) {
                  console.error('Error archiving job:', error);
                } finally {
                  loadingAction(false);
                }
              }}
              formRef={formRef}
            />
          </Box>
        ),
      },
    },
  ];
  return config;
};

// New filter config format for the new Filter design
export const jobsNewFilterConfig = (offices, departments, users) => {
  const statusOptions = Object.entries(jobStatusConfig).map(([key, value]) => ({
    value: key,
    label: value.label,
    id: key,
  }));

  const usersOptions = users
    ? users.map((user) => ({
        ...user,
        label: user?.name,
        value: user?.id,
      }))
    : [];

  const officeOptions = Array.isArray(offices)
    ? offices.map((item) => ({
        ...item,
        label: item?.name,
        value: item?.id,
      }))
    : [];

  const departmentOptions = Array.isArray(departments) ? departments : [];

  // User filters - only include if users are available
  const userFilters =
    usersOptions?.length > 0
      ? {
          user_ids: {
            filterLabel: 'User',
            name: 'user_ids',
            labelField: 'name',
            label: '',
            isMulti: true,
            isCompare: false,
            isSearchable: true,
            isBoolean: false,
            isRequired: false,
            customFormatLabel: SelectOptionWithAvatar,
            options: usersOptions,
            validationType: 'array',
            type: 'select',
            isNewFilter: true,
            isAppendToBody: true,
            placeholder: 'Search user',
          },
          primary_recruiter_ids: {
            filterLabel: 'Primary recruiter',
            name: 'primary_recruiter_ids',
            labelField: 'name',
            label: '',
            isMulti: true,
            isCompare: false,
            isSearchable: true,
            isBoolean: false,
            isRequired: false,
            customFormatLabel: SelectOptionWithAvatar,
            options: usersOptions,
            validationType: 'array',
            type: 'select',
            isNewFilter: true,
            isAppendToBody: true,
            placeholder: 'Search recruiter',
            section: 'Role-based filters',
          },
          primary_hiring_manager_ids: {
            filterLabel: 'Primary hiring manager',
            name: 'primary_hiring_manager_ids',
            labelField: 'name',
            label: '',
            isMulti: true,
            isCompare: false,
            isSearchable: true,
            isBoolean: false,
            isRequired: false,
            customFormatLabel: SelectOptionWithAvatar,
            options: usersOptions,
            validationType: 'array',
            type: 'select',
            isNewFilter: true,
            isAppendToBody: true,
            placeholder: 'Search hiring manager',
            section: 'Role-based filters',
          },
        }
      : {};

  return {
    department_ids: {
      filterLabel: 'Department',
      name: 'department_ids',
      isMulti: true,
      isCompare: false,
      isSearchable: true,
      isBoolean: false,
      isRequired: false,
      options: departmentOptions,
      valueKey: 'id',
      labelKey: 'name',
      validationType: 'array',
      components: {
        Menu: SelectParentChildRootOption,
        MultiValue: DepartmentMultiValue,
      },
      type: 'select',
      isNewFilter: true,
      isAppendToBody: true,
      placeholder: 'Search department',
    },
    office_ids: {
      filterLabel: 'Office',
      name: 'office_ids',
      isMulti: true,
      isCompare: false,
      isSearchable: true,
      isBoolean: false,
      isRequired: false,
      options: officeOptions,
      valueKey: 'id',
      labelKey: 'name',
      validationType: 'array',
      type: 'select',
      isNewFilter: true,
      isAppendToBody: true,
      placeholder: 'Search office',
    },
    job_statuses: {
      filterLabel: 'Status',
      name: 'job_statuses',
      labelField: 'label',
      label: '',
      isMulti: true,
      isCompare: false,
      isSearchable: true,
      isBoolean: false,
      isRequired: false,
      options: statusOptions,
      validationType: 'array',
      type: 'select',
      isNewFilter: true,
      isAppendToBody: true,
      placeholder: 'Search status',
    },
    eeoc_data_collection: {
      filterLabel: 'EEOC data collection',
      name: 'eeoc_data_collection',
      labelField: 'label',
      label: '',
      isMulti: false,
      isCompare: false,
      isSearchable: false,
      isBoolean: true,
      isRequired: false,
      options: [
        { value: 'true', label: 'Enabled' },
        { value: 'false', label: 'Disabled' },
      ],
      validationType: 'object',
      type: 'select',
      isNewFilter: true,
      isAppendToBody: true,
      placeholder: 'Select',
    },
    ...userFilters,
    job_post_listed: {
      filterLabel: 'Job posting visibility',
      name: 'job_post_listed',
      labelField: 'label',
      label: '',
      isMulti: false,
      isCompare: false,
      isSearchable: false,
      isBoolean: true,
      isRequired: false,
      options: [
        { value: 'true', label: 'Listed', id: 'true' },
        { value: 'false', label: 'Unlisted', id: 'false' },
      ],
      validationType: 'object',
      type: 'select',
      isNewFilter: true,
      isAppendToBody: true,
      placeholder: 'Select visibility',
    },
    employment_types: {
      filterLabel: 'Work type',
      name: 'employment_types',
      labelField: 'label',
      label: '',
      isMulti: true,
      isCompare: false,
      isSearchable: true,
      options: jobsEmploymentsOptions,
      validationType: 'array',
      type: 'select',
      isNewFilter: true,
      isAppendToBody: true,
      placeholder: 'Search work type',
    },
    workplace: {
      filterLabel: 'Workplace',
      name: 'workplace',
      labelField: 'label',
      label: '',
      isMulti: true,
      isCompare: false,
      isSearchable: false,
      isBoolean: false,
      isRequired: false,
      options: [
        { value: 'remote', label: 'Remote', id: 'remote' },
        { value: 'office', label: 'Office', id: 'office' },
        { value: 'hybrid', label: 'Hybrid', id: 'hybrid' },
      ],
      validationType: 'array',
      type: 'select',
      isNewFilter: true,
      isAppendToBody: true,
      placeholder: 'Select workplace',
    },
    job_post_internal_option: {
      filterLabel: 'Job post',
      name: 'job_post_internal_option',
      labelField: 'label',
      label: '',
      isMulti: true,
      isCompare: false,
      isSearchable: false,
      isBoolean: false,
      isRequired: false,
      options: [
        { value: 'internal_only', label: 'Internal', id: 'internal_only' },
        { value: 'external_only', label: 'External', id: 'external_only' },
      ],
      validationType: 'array',
      type: 'select',
      isNewFilter: true,
      isAppendToBody: true,
      placeholder: 'Select job post type',
    },
    confidential: {
      filterLabel: 'Confidential',
      name: 'confidential',
      labelField: 'label',
      label: '',
      isMulti: false,
      isCompare: false,
      isSearchable: false,
      isBoolean: true,
      isRequired: false,
      validationType: 'object',
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ],
      type: 'select',
      isNewFilter: true,
      isAppendToBody: true,
      placeholder: 'Select',
    },
  };
};
